import { ParamMap } from '@angular/router';
import { environment } from '../../../../environments/environment';

export function getDefaultIsDummy(): boolean {
  return false; // always false for now, will be updated later, use environment.production for production
}

export function parseQueryIsDummy(value: string | null, fallback: boolean): boolean {
  if (value === '1') return true;
  if (value === '0') return false;
  return fallback;
}

export function buildIsDummyQueryParam(isDummy: boolean, defaultIsDummy: boolean): number | null {
  return isDummy !== defaultIsDummy ? (isDummy ? 1 : 0) : null;
}

export function parseQueryInteger(value: string | null, fallback: number): number {
  if (value === null || value === '') {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** FM / Credee / Recuvery list filter state mirrored in URL query params. */
export interface FmListRouteState {
  filter: number;
  status: string;
  partnerStatus: string;
  challenge: string;
  orp: number;
  instant: number;
  vip: number;
  urgent: boolean;
  search: string;
  page: number;
  countryId?: number;
  lendeeShareType?: number;
  lendeePaymentStatus?: number;
  startDate?: string;
  endDate?: string;
  isDummy: boolean;
}

export interface FmListRouteDefaults {
  filter: number;
  status?: string;
  partnerStatus?: string;
  challenge?: string;
  orp?: number;
  instant?: number;
  vip?: number;
  urgent?: boolean;
  search?: string;
  page?: number;
  countryId?: number;
  lendeeShareType?: number;
  lendeePaymentStatus?: number;
  startDate?: string;
  endDate?: string;
  supportedFilters?: readonly number[];
  includeCountry?: boolean;
  includeDateRange?: boolean;
  includeLendeeFilters?: boolean;
  lendeeFilter?: number;
  /** Query param for lendee beneficiary filter; Credee uses `beneficiary_type`. */
  lendeeShareQueryParam?: string;
  isDummy?: boolean;
}

function parseLendeeFilterInteger(value: string | null, fallback: number): number {
  const parsed = parseQueryInteger(value, fallback);
  return parsed >= 0 && parsed <= 2 ? parsed : fallback;
}

export function parseFmListRouteState(params: ParamMap, defaults: FmListRouteDefaults): FmListRouteState {
  const supported = defaults.supportedFilters;
  let filter = parseQueryInteger(params.get('filter'), defaults.filter);
  if (supported?.length && !supported.includes(filter)) {
    filter = defaults.filter;
  }

  const state: FmListRouteState = {
    filter,
    status: params.get('status') || defaults.status || '0',
    partnerStatus: params.get('partner_status') || defaults.partnerStatus || '0',
    challenge: params.get('challenge') || defaults.challenge || '0',
    orp: parseQueryInteger(params.get('orp'), defaults.orp ?? -1),
    instant: parseQueryInteger(params.get('instant'), defaults.instant ?? -1),
    vip: parseQueryInteger(params.get('vip'), defaults.vip ?? 0),
    urgent: params.get('urgent') === '1',
    search: params.get('search') || defaults.search || '',
    page: Math.max(1, parseQueryInteger(params.get('page'), defaults.page ?? 1)),
    isDummy: parseQueryIsDummy(params.get('is_dummy'), defaults.isDummy ?? getDefaultIsDummy()),
  };

  if (defaults.includeCountry) {
    state.countryId = parseQueryInteger(params.get('country'), defaults.countryId ?? 0);
  }

  if (defaults.includeDateRange) {
    state.startDate = params.get('start') || defaults.startDate || '';
    state.endDate = params.get('end') || defaults.endDate || '';
  }

  if (defaults.includeLendeeFilters) {
    const shareParam = defaults.lendeeShareQueryParam ?? 'lendee_share';
    const legacyShareParam = shareParam === 'beneficiary_type' ? 'lendee_share' : null;
    state.lendeeShareType = parseLendeeFilterInteger(
      params.get(shareParam) ?? (legacyShareParam ? params.get(legacyShareParam) : null),
      defaults.lendeeShareType ?? 0
    );
    state.lendeePaymentStatus = parseLendeeFilterInteger(
      params.get('lendee_status'),
      defaults.lendeePaymentStatus ?? 0
    );
  }

  return state;
}

export function buildFmListQueryParams(
  state: FmListRouteState,
  defaults: FmListRouteDefaults
): Record<string, string | number | null> {
  const queryParams: Record<string, string | number | null> = {
    filter: state.filter,
    status: state.status !== (defaults.status ?? '0') ? state.status : null,
    partner_status: state.partnerStatus !== (defaults.partnerStatus ?? '0') ? state.partnerStatus : null,
    challenge: state.challenge !== (defaults.challenge ?? '0') ? state.challenge : null,
    orp: state.orp !== (defaults.orp ?? -1) ? state.orp : null,
    instant: state.instant !== (defaults.instant ?? -1) ? state.instant : null,
    vip: state.vip !== (defaults.vip ?? 0) ? state.vip : null,
    urgent: state.urgent ? 1 : null,
    search: state.search.trim() ? state.search.trim() : null,
    page: state.page > 1 ? state.page : null,
    is_dummy: buildIsDummyQueryParam(state.isDummy, defaults.isDummy ?? getDefaultIsDummy()),
  };

  if (defaults.includeCountry && state.countryId !== undefined) {
    queryParams['country'] = state.countryId !== (defaults.countryId ?? 0) ? state.countryId : null;
  }

  if (defaults.includeDateRange && state.startDate !== undefined && state.endDate !== undefined) {
    queryParams['start'] = state.startDate !== defaults.startDate ? state.startDate : null;
    queryParams['end'] = state.endDate !== defaults.endDate ? state.endDate : null;
  }

  if (defaults.includeLendeeFilters && defaults.lendeeFilter != null) {
    const onLendeeFilter = state.filter === defaults.lendeeFilter;
    const shareParam = defaults.lendeeShareQueryParam ?? 'lendee_share';
    queryParams[shareParam] =
      onLendeeFilter && state.lendeeShareType !== (defaults.lendeeShareType ?? 0)
        ? state.lendeeShareType ?? 0
        : null;
    if (shareParam === 'beneficiary_type') {
      queryParams['lendee_share'] = null;
    }
    queryParams['lendee_status'] =
      onLendeeFilter && state.lendeePaymentStatus !== (defaults.lendeePaymentStatus ?? 0)
        ? state.lendeePaymentStatus ?? 0
        : null;
  }

  return queryParams;
}

/** Lendee list filter state mirrored in URL query params. */
export interface LendeeListRouteState {
  search: string;
  page: number;
  subStatus: number;
  startDate: string;
  endDate: string;
  isDummy: boolean;
}

export interface LendeeListRouteDefaults {
  search?: string;
  page?: number;
  subStatus?: number;
  startDate: string;
  endDate: string;
  isDummy?: boolean;
}

export function parseLendeeListRouteState(params: ParamMap, defaults: LendeeListRouteDefaults): LendeeListRouteState {
  return {
    search: params.get('search') || defaults.search || '',
    page: Math.max(1, parseQueryInteger(params.get('page'), defaults.page ?? 1)),
    subStatus: parseQueryInteger(params.get('sub_status'), defaults.subStatus ?? -1),
    startDate: params.get('start') || defaults.startDate,
    endDate: params.get('end') || defaults.endDate,
    isDummy: parseQueryIsDummy(params.get('is_dummy'), defaults.isDummy ?? getDefaultIsDummy()),
  };
}

export function buildLendeeListQueryParams(
  state: LendeeListRouteState,
  defaults: LendeeListRouteDefaults
): Record<string, string | number | null> {
  return {
    search: state.search.trim() ? state.search.trim() : null,
    page: state.page > 1 ? state.page : null,
    sub_status: state.subStatus !== (defaults.subStatus ?? -1) ? state.subStatus : null,
    start: state.startDate !== defaults.startDate ? state.startDate : null,
    end: state.endDate !== defaults.endDate ? state.endDate : null,
    is_dummy: buildIsDummyQueryParam(state.isDummy, defaults.isDummy ?? getDefaultIsDummy()),
  };
}
